import React, { useState, useEffect, useCallback } from 'react';
import { Navigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { 
  Add, DeleteForever 
} from '@mui/icons-material';
import { Tooltip, Fab } from "@mui/material";

import Grid from '@mui/material/Grid2';

import { 
  ModelBase, 
} from "@models";
import { 
  Transform,
  CreatureTransform,
  TransformInterface,
  UpgradableNumber,
  CreaturePotence,
} from "@dndModels";
import { 
  CHARACTER_LEVELS,
  CREATURE_TYPES, 
  SIZE_OPTIONS, 
  SPEED_OPTIONS 
} from "@dndModels/Constants";

import API from "@dndUtilities/system_api";
import CommonUtilities from "@utilities/common_utilities";

import LoadingBox from "@components/input/LoadingBox";

import CenteredMenu from "@components/input/menus/CenteredMenu";
import StringBox from "@components/input/strings/StringBox";
import ModelBaseDetails from "@dndComponents/ModelBaseDetails";
import TransformInterfaceInput from "@dndComponents/other/TransformInterfaceInput";
import EditableLabel from "@components/input/strings/EditableLabel";
import UpgradableNumberBox from "@dndComponents/UpgradableNumberBox";
import ToggleButtonBox from "@components/input/buttons/ToggleButtonBox";
import SelectObjectBox from "@components/input/selects/SelectObjectBoxSimple";
import SelectStringBox from "@components/input/selects/SelectStringBox";
import FilterBox from "@dndComponents/filtering/FilterBox";
import SuggestionStringBox from "@dndComponents/select/SuggestionStringBox";
import ArrowBackButton from '@components/input/buttons/icon_buttons/ArrowBackButton';

const api = API.getInstance();
const common_util = CommonUtilities.getInstance();

interface Props {
  obj?: ModelBase;
}

export default function TransformEdit({ obj: param_obj }: Props) {
  const [is_loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [obj, setObj] = useState<Transform>(param_obj instanceof Transform ? param_obj : api.applyWorkingOn(new Transform()));
  const [change_time, setChangeTime] = useState(0);
  const [tab, setTab] = useState("Description");
  const [filter, setFilter] = useState({});
  const [fields, setFields] = useState<string[]>([]);
  const [navigateTo, setNavigateTo] = useState<string | null>(null);
  
  const disabled = api.user === null || !api.online || (!api.user.admin && obj._id !== "" && obj.owner_id !== api.user._id);

  const load = useCallback( async () => {
    // debugger;
    if (obj._id !== "") {
      if (obj instanceof CreatureTransform) {
        cleanFilter(obj.knowledge_type, {...obj.obj_filter});
        getFields(obj.knowledge_type);
      }
    }
    setLoading(false);
  }, [obj]);

  useEffect(() => {
    if(!fetched) {
      setFetched(true);
      setLoading(true);
      setTimeout(() => {
        load();
      }, 100);
    }
  }, [fetched, load]);
    
  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  const cleanFilter = (knowledge_type: string, new_filter: any) => {
    if (new_filter["id"]) {
      switch (knowledge_type) {
        case "Adaptation": new_filter["adaptation"] = new_filter["id"]; break;
        case "Transform": new_filter["transform"] = new_filter["id"]; break;
        case "Creature": new_filter["creature"] = new_filter["id"]; break;
      }
      delete new_filter["id"];
    }
    setFilter(new_filter);
    setReloading(true);
  }
  
  const updateFilter = (changed: any = null) => {
    // debugger;
    if (obj instanceof CreatureTransform) {
      const new_obj = obj;
      const new_filter = {...changed};
      switch (obj.knowledge_type) {
        case "Adaptation":
          if (new_filter["adaptation"]) {
            new_filter["id"] = new_filter["adaptation"];
            delete new_filter["adaptation"];
          }
        break;
        case "Transform": 
          if (new_filter["transform"]) {
            new_filter["id"] = new_filter["transform"];
            delete new_filter["transform"];
          }
        break;
        case "Creature": 
          if (new_filter["creature"]) {
            new_filter["id"] = new_filter["creature"];
            delete new_filter["creature"];
          }
        break;
      }
      new_obj.obj_filter = new_filter;
      setObj(new_obj);
      setChangeTime(common_util.get_time_number());
    }
  }

  const getFields = (type: string) => {
    switch (type) {
      case "Adaptation": setFields(["source_book","transform_type","adaptation"]); break;
      case "Transform": setFields(["source_book","transform_type","transform"]); break;
      case "Creature": setFields([
        "source_book",
        "creature_type",
        "subtype",
        "min_cr",
        "max_cr",
        "flying",
        "swimming",
        "creature_size",
        "environments",
        "settings",
        "native_planes",
        "effect_type",
        "attack_type",
        "creature"]); break;
      default: setFields([]); break;
    }
  }

  const renderTab = () => {
    switch (tab) {
      case "Description": return renderDescription();
      // case "Ability Scores": return renderAbilityScores();
      // case "Attributes": return renderAttributes();
      // case "Abilities": return renderAbilities();
      // case "Resources": return renderResources();
      // case "Proficiencies": return renderProficiencies();
      // case "Senses": return renderSenses();
      // case "Speeds": return renderSpeeds();
      // case "Common Features": return renderTransformFeatures();
      default: 
        return (
          <TransformInterfaceInput
            obj={obj}
            disabled={disabled}
            tab={tab}
            change_time={change_time}
            can_creature={obj instanceof CreatureTransform}
            onChange={(changed: TransformInterface, log_it: boolean) => {
              const new_obj = obj;
              new_obj.copyInterface(changed, log_it);
              setObj(new_obj);
            }}
          />
        );
    }
  }

  const renderCreaturePotences = (creature_transform: CreatureTransform) => {
    return (
      <>
        <Grid
          size={{
            xs: creature_transform.potence_type === "Class" ? 6 : 12
          }}>
          <SelectStringBox
            name="Potence Type"
            options={["Character","Class","None"]}
            value={creature_transform.potence_type}
            onChange={(changed: string) => {
              const new_obj = obj as CreatureTransform;
              new_obj.potence_type = changed;
              setObj(new_obj);
              setChangeTime(common_util.get_time_number());
            }}
          />
        </Grid>
        { creature_transform.potence_type === "Class" && 
          <Grid
            size={{
              xs: 6
            }}>
            <SelectObjectBox
              name="Class"
              data_type="game_class"
              value={creature_transform.potence_class}
              onChange={(changed: string) => {
                const new_obj = obj as CreatureTransform;
                new_obj.potence_class = changed;
                setObj(new_obj);
                setChangeTime(common_util.get_time_number());
              }}
            />
          </Grid>
        }
        <Grid
          size={{
            xs: 12
          }}>
          <span className="header">
            Potences
          </span>
          { !disabled &&
            <Tooltip title={`Add Potence`}>
              <Fab size="small" color="primary" style={{marginLeft: "8px"}}
                onClick={ () => {
                  const new_obj = obj as CreatureTransform;
                  const potence: CreaturePotence = {
                    true_id: uuidv4().toString(),
                    level: 1,
                    specific_id: [],
                    creature_type: [], 
                    max_cr: new UpgradableNumber(),
                    size: [],
                    speed: []
                  };
                  if (new_obj.creature_potences.length > 0) {
                    const last = new_obj.creature_potences[new_obj.creature_potences.length - 1];
                    potence.level = last.level + 1;
                    potence.specific_id = [...last.specific_id];
                    potence.creature_type = [...last.creature_type];
                    potence.max_cr = last.max_cr.clone();
                    potence.size = [...last.size];
                    potence.speed = [...last.speed];
                  }
                  new_obj.creature_potences.push(potence);
                  setObj(new_obj);
                  setChangeTime(common_util.get_time_number());
                }}>
                <Add/>
              </Fab>
            </Tooltip>
          }
        </Grid>
        { creature_transform.creature_potences.map((p, key) => {
          return (
            <Grid
              key={key}
              container
              spacing={0}
              direction="row"
              size={{
                xs: 12
              }}>
              <Grid
                size={{
                  xs: 2
                }}>
                <Tooltip title={`Delete Potence`}>
                  <Fab size="small" color="primary" style={{marginLeft: "8px"}}
                    onClick={ () => {
                      const new_obj = obj as CreatureTransform;
                      new_obj.creature_potences = new_obj.creature_potences.filter(o => o.true_id !== p.true_id);
                      setObj(new_obj);
                      setChangeTime(common_util.get_time_number());
                    }}>
                    <DeleteForever/>
                  </Fab>
                </Tooltip>
              </Grid>
              <Grid
                size={{
                  xs: 3
                }}>
                <SelectStringBox 
                  name="Level"
                  options={CHARACTER_LEVELS}
                  value={`${p.level}`} 
                  onChange={(changed: string) => {
                    const new_obj = obj as CreatureTransform;
                    const potence = new_obj.creature_potences.find(o => o.true_id === p.true_id);
                    if (potence) {
                      potence.level = +changed;
                      setObj(new_obj);
                      setChangeTime(common_util.get_time_number());
                    }
                  }}
                />
              </Grid>
              <Grid
                size={{
                  xs: 3
                }}>
                <SelectStringBox
                  name="Creature Types"
                  options={CREATURE_TYPES}
                  values={p.creature_type}
                  multiple
                  onChange={(changed: string[]) => {
                    const new_obj = obj as CreatureTransform;
                    const potence = new_obj.creature_potences.find(o => o.true_id === p.true_id);
                    if (potence) {
                      potence.creature_type = changed;
                      setObj(new_obj);
                      setChangeTime(common_util.get_time_number());
                    }
                  }}
                />
              </Grid>
              <Grid
                size={{
                  xs: 4
                }}>
                <UpgradableNumberBox
                  value={ p.max_cr } 
                  name="Maximum CR"
                  disabled={disabled}
                  onChange={(changed: UpgradableNumber) => {
                    const new_obj = obj as CreatureTransform;
                    const potence = new_obj.creature_potences.find(o => o.true_id === p.true_id);
                    if (potence) {
                      potence.max_cr = changed.clone();
                      setObj(new_obj);
                      setChangeTime(common_util.get_time_number());
                    }
                  }}
                />
              </Grid>
              <Grid
                size={{
                  xs: 3
                }}>
                <SelectStringBox
                  name="Allowed Sizes"
                  options={SIZE_OPTIONS}
                  values={p.size}
                  multiple
                  onChange={(changed: string[]) => {
                    const new_obj = obj as CreatureTransform;
                    const potence = new_obj.creature_potences.find(o => o.true_id === p.true_id);
                    if (potence) {
                      potence.size = changed;
                      setObj(new_obj);
                      setChangeTime(common_util.get_time_number());
                    }
                  }}
                />
              </Grid>
              <Grid
                size={{
                  xs: 3
                }}>
                <SelectStringBox
                  name="Allowed Speeds"
                  options={SPEED_OPTIONS}
                  values={p.speed}
                  multiple
                  onChange={(changed: string[]) => {
                    const new_obj = obj as CreatureTransform;
                    const potence = new_obj.creature_potences.find(o => o.true_id === p.true_id);
                    if (potence) {
                      potence.speed = changed;
                      setObj(new_obj);
                      setChangeTime(common_util.get_time_number());
                    }
                  }}
                />
              </Grid>
              <Grid
                size={{
                  xs: 3
                }}>
                <SuggestionStringBox 
                  name="Age Category"
                  field="age_category"
                  value={p.age_category !== undefined ? p.age_category : ""}
                  disabled={disabled}
                  onChange={(changed: string) => {
                    const new_obj = obj as CreatureTransform;
                    const potence = new_obj.creature_potences.find(o => o.true_id === p.true_id);
                    if (potence) {
                      potence.age_category = changed;
                      setObj(new_obj);
                      setChangeTime(common_util.get_time_number());
                    }
                  }}
                />
              </Grid>
              <Grid
                size={{
                  xs: 3
                }}>
                <StringBox
                  name="Filter Field from Item Extra"
                  value={p.filter_field_from_extra !== undefined ? p.filter_field_from_extra : ""}
                  onBlur={(changed: string) => {
                    const new_obj = obj as CreatureTransform;
                    const potence = new_obj.creature_potences.find(o => o.true_id === p.true_id);
                    if (potence) {
                      potence.filter_field_from_extra = changed;
                      setObj(new_obj);
                      setChangeTime(common_util.get_time_number());
                    }
                  }}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12
                }}>
                <SelectObjectBox
                  name="Specific Creatures"
                  data_type="creature"
                  values={p.specific_id}
                  multiple
                  // internal_filter
                  limit={100}
                  onChange={(changed: string[]) => {
                    const new_obj = obj as CreatureTransform;
                    const potence = new_obj.creature_potences.find(o => o.true_id === p.true_id);
                    if (potence) {
                      potence.specific_id = changed;
                      setObj(new_obj);
                      setChangeTime(common_util.get_time_number());
                    }
                  }}
                />
              </Grid>
            </Grid>
          );
        })}
      </>
    );
  }

  const renderDescription = () => {
    return (
      <ModelBaseDetails
        obj={obj}
        disabled={disabled}
        change_time={change_time}
        onChange={(changed: ModelBase) => {
          if (changed instanceof CreatureTransform) {
            const new_obj = obj;
            new_obj.copy(changed);
            setObj(new_obj);
            setChangeTime(common_util.get_time_number());
          } else if (changed instanceof Transform) {
            const new_obj = obj;
            new_obj.copy(changed);
            setObj(new_obj);
            setChangeTime(common_util.get_time_number());
          }
        }}>
        <Grid container spacing={0} direction="row">
          <Grid
            size={{
              xs: 12
            }}>
            <StringBox
              name="Transform Type"
              value={obj.transform_type}
              onBlur={(changed: string) => {
                const new_obj = obj;
                new_obj.transform_type = changed;
                setObj(new_obj);
                setChangeTime(common_util.get_time_number());
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 12
            }}>
            <StringBox
              name="Cost"
              value={`${obj.cost}`}
              type="number"
              onBlur={(changed: string) => {
                const new_obj = obj;
                new_obj.cost = +changed;
                setObj(new_obj);
                setChangeTime(common_util.get_time_number());
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 12
            }}>
            <ToggleButtonBox
              name="Creature Transform"
              value={obj instanceof CreatureTransform}
              onToggle={() => {
                if (obj instanceof CreatureTransform) {
                  const new_obj = new Transform();
                  new_obj.copy(obj);
                  
                  setObj(new_obj);
                  setChangeTime(common_util.get_time_number());
                } else {
                  const new_obj = new CreatureTransform();
                  new_obj.copyTransform(obj);
                  
                  setObj(new_obj);
                  setChangeTime(common_util.get_time_number());
                }
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 12
            }}>
            <span className="header">
              Formulae
            </span>
            { !disabled &&
              <Tooltip title={`Create New Formula`}>
                <Fab size="small" color="primary" style={{marginLeft: "8px"}}
                  onClick={ () => {
                    const new_obj = obj;
                    new_obj.formulae["New Formula"] = new UpgradableNumber();
                    setObj(new_obj);
                    setChangeTime(common_util.get_time_number());
                    setReloading(true);
                  }}>
                  <Add/>
                </Fab>
              </Tooltip>
            }
          </Grid>
          { obj instanceof CreatureTransform && 
            <>
              <Grid
                size={{
                  xs: obj.knowledge_type === "None" ? 12 : 4
                }}>
                <SelectStringBox 
                  name="Knowledge Type"
                  disabled={disabled}
                  options={["Creature","Transform","Spell","None"]}
                  value={obj.knowledge_type}
                  onChange={(changed: string) => {
                    const new_obj = obj;
                    (new_obj.knowledge_type as string) = changed;
                    new_obj.obj_filter = {};
                    new_obj.knowledge_usage_key = "";                  
                    getFields(changed);
                    setObj(new_obj);
                    setChangeTime(common_util.get_time_number());
                    setReloading(true);
                  }}
                /> 
              </Grid>
              { obj.knowledge_type !== "None" &&
                <>
                  <Grid
                    size={{
                      xs: 4
                    }}>
                    <StringBox 
                      name="Knowledge Usage Key"
                      disabled={disabled}
                      value={obj.knowledge_usage_key}
                      onBlur={(value: string) => {
                        const new_obj = obj;
                        new_obj.knowledge_usage_key = value;
                        setObj(new_obj);
                        setChangeTime(common_util.get_time_number());
                      }}
                    /> 
                  </Grid>
                  <Grid
                    size={{
                      xs: 4
                    }}>
                    { !reloading &&
                      <FilterBox
                        the_filter={filter}
                        fields={fields}
                        dont_use_api
                        // log_it
                        disabled={disabled}
                        onChange={updateFilter}
                      />
                    }
                  </Grid>
                </>
              }
            </>
          }
          { obj instanceof CreatureTransform && renderCreaturePotences(obj) }
          { reloading ? 
            <Grid>Reloading</Grid> 
          : Object.keys(obj.formulae).sort((a, b) => {
            return a.localeCompare(b);
          }).map((formula_key, key) => {
            return (
              <Grid
                key={key}
                container
                spacing={0}
                direction="row"
                size={{
                  xs: 12
                }}>
                <Grid
                  size={{
                    xs: 2
                  }}>
                  <EditableLabel 
                    name="Formula Key"
                    value={formula_key}
                    onChange={(changed: string) => {
                      const new_obj = obj;
                      const old_value = new_obj.formulae[formula_key].clone();
                      delete new_obj.formulae[formula_key];
                      new_obj.formulae[changed] = old_value;
                      setObj(new_obj);
                      setChangeTime(common_util.get_time_number());
                      setReloading(true);
                    }}
                  />
                </Grid>
                <Grid
                  size={{
                    xs: 10
                  }}>
                  <UpgradableNumberBox 
                    value={ obj.formulae[formula_key] as UpgradableNumber } 
                    name={ formula_key }
                    disabled={disabled}
                    onChange={(changed: UpgradableNumber) => {
                      const new_obj = obj;
                      new_obj.formulae[formula_key] = changed;
                      setObj(new_obj);
                      setChangeTime(common_util.get_time_number());
                    }}
                  />
                </Grid>
              </Grid>
            );
          })}
        </Grid>
      </ModelBaseDetails>
    );
  }

  const height = window.innerHeight - 64;
  if (navigateTo !== null) {
    return <Navigate to={navigateTo} />;
  } else if (!fetched || is_loading) {
    return (<LoadingBox size={height * 0.5} height={height} width={window.innerWidth} />);
  } else {
    return (
      <Grid container spacing={0} direction="row">
        <Grid size={12}>
          <ArrowBackButton
            fab
            onClick={ () => {
              setNavigateTo(`/transform`);
            }}
          />
        </Grid>
        <Grid size={12}>
          <StringBox 
            value={obj.name} 
            message={obj.name.length > 0 ? "" : "Name Invalid"}
            name="Name"
            disabled={disabled}
            onBlur={(value: string) => {
              const new_obj = obj;
              new_obj.name = value;
              setObj(new_obj);
              setChangeTime(common_util.get_time_number());
            }}
          />
        </Grid>
        <Grid size={12}>
          <CenteredMenu
            options={["Description","Ability Scores","Attributes","Abilities","Resources","Senses","Speeds","Common Features"]}
            selected={tab}
            onSelect={setTab}
          />
        </Grid>
        <Grid size={12}>
          { renderTab() }
        </Grid>
      </Grid>
    ); 
  }
}